import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  ref,
  get,
  onDisconnect,
  onValue,
  push,
  remove,
  set,
} from "firebase/database";

const configuration = {
  iceServers: [
    {
      urls: "stun:stun.l.google.com:19302",
    },
  ],
};

const VideoCallPage = () => {
  const [searchParams] = useSearchParams();
  const questionId = searchParams.get("id");
  const navigate = useNavigate();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const pendingCandidates = useRef([]); // ✅ 缓存候选人111

  const [status, setStatus] = useState("Connecting...");

  useEffect(() => {
    start();
    return () => cleanup();
    // eslint-disable-next-line
  }, []);

  const start = async () => {
    pcRef.current = new RTCPeerConnection(configuration);

    const localStream = await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });

    localStreamRef.current = localStream;
    localStream.getTracks().forEach((track) => {
      pcRef.current.addTrack(track, localStream);
    });

    localVideoRef.current.srcObject = localStream;

    // ICE candidates
    pcRef.current.onicecandidate = (event) => {
      if (event.candidate) {
        const candidateRef = ref(db, `calls/${questionId}/candidates`);
        const newCandidate = push(candidateRef);
        set(newCandidate, event.candidate.toJSON());
      }
    };

    // When receiving remote stream
    pcRef.current.ontrack = (event) => {
      const [remoteStream] = event.streams;
      remoteVideoRef.current.srcObject = remoteStream;
    };

    // Check if offer already exists
    const offerSnapshot = await get(ref(db, `calls/${questionId}/offer`));
    if (!offerSnapshot.exists()) {
      const offer = await pcRef.current.createOffer();
      await pcRef.current.setLocalDescription(offer);
      await set(ref(db, `calls/${questionId}/offer`), offer);
    } else {
      const offer = offerSnapshot.val();
      await pcRef.current.setRemoteDescription(
        new RTCSessionDescription(offer)
      );
      const answer = await pcRef.current.createAnswer();
      await pcRef.current.setLocalDescription(answer);
      await set(ref(db, `calls/${questionId}/answer`), answer);
      setStatus("Call Connected");

      // ✅ 尝试添加之前缓存的候选人
      for (const c of pendingCandidates.current) {
        try {
          await pcRef.current.addIceCandidate(new RTCIceCandidate(c));
        } catch (err) {
          console.warn("Error adding pending ICE candidate:", err);
        }
      }
      pendingCandidates.current = []; // 清空缓存
    }

    // Listen for answer if this user is the offerer
    const answerRef = ref(db, `calls/${questionId}/answer`);
    onValue(answerRef, async (snapshot) => {
      const answer = snapshot.val();
      if (answer && !pcRef.current.currentRemoteDescription) {
        await pcRef.current.setRemoteDescription(
          new RTCSessionDescription(answer)
        );
        setStatus("Call Connected");

        // ✅ 添加候选人
        for (const c of pendingCandidates.current) {
          try {
            await pcRef.current.addIceCandidate(new RTCIceCandidate(c));
          } catch (err) {
            console.warn("Error adding pending ICE candidate:", err);
          }
        }
        pendingCandidates.current = [];
      }
    });

    // Add ICE candidates from remote
    const candidateRef = ref(db, `calls/${questionId}/candidates`);
    onValue(candidateRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        Object.values(data).forEach((c) => {
          if (
            pcRef.current?.remoteDescription &&
            pcRef.current.remoteDescription.type
          ) {
            pcRef.current
              .addIceCandidate(new RTCIceCandidate(c))
              .catch((err) => console.warn("Error adding ICE candidate:", err));
          } else {
            // ❗尚未 setRemoteDescription，先存起来
            pendingCandidates.current.push(c);
          }
        });
      }
    });

    // Auto cleanup on disconnect
    onDisconnect(ref(db, `calls/${questionId}`)).remove();
  };

  const cleanup = async () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
    }
    if (pcRef.current) {
      pcRef.current.close();
    }
    await remove(ref(db, `calls/${questionId}`));
  };

  const handleLeave = () => {
    cleanup().then(() => {
      navigate(`/`);
    });
  };

  return (
    <div
      style={{
        height: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        flexDirection: "column",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* 顶部状态栏 */}
      <div
        style={{
          background: "rgba(0, 0, 0, 0.2)",
          backdropFilter: "blur(10px)",
          padding: "20px",
          textAlign: "center",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <h2
          style={{
            color: "white",
            margin: "0 0 10px 0",
            fontSize: "24px",
            fontWeight: "600",
          }}
        >
          📞 Video Call
        </h2>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            background:
              status === "Call Connected"
                ? "rgba(34, 197, 94, 0.2)"
                : "rgba(251, 191, 36, 0.2)",
            border: `1px solid ${
              status === "Call Connected"
                ? "rgba(34, 197, 94, 0.3)"
                : "rgba(251, 191, 36, 0.3)"
            }`,
            borderRadius: "20px",
            padding: "8px 16px",
            color: "white",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          <div
            style={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: status === "Call Connected" ? "#22c55e" : "#fbbf24",
              marginRight: "8px",
              animation:
                status === "Call Connected" ? "none" : "pulse 2s infinite",
            }}
          ></div>
          {status}
        </div>
      </div>

      {/* 主要视频区域 */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "30px",
          padding: "40px 20px",
          position: "relative",
        }}
      >
        {/* 远程视频 (主要) */}
        <div
          style={{
            position: "relative",
            borderRadius: "20px",
            overflow: "hidden",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
            background: "rgba(0, 0, 0, 0.1)",
            border: "2px solid rgba(255, 255, 255, 0.1)",
          }}
        >
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            style={{
              width: "500px",
              height: "375px",
              objectFit: "cover",
              display: "block",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "15px",
              left: "15px",
              background: "rgba(0, 0, 0, 0.6)",
              color: "white",
              padding: "8px 12px",
              borderRadius: "10px",
              fontSize: "14px",
              fontWeight: "500",
              backdropFilter: "blur(5px)",
            }}
          >
            👥 Partner
          </div>
        </div>

        {/* 本地视频 (画中画) */}
        <div
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            borderRadius: "15px",
            overflow: "hidden",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
            background: "rgba(0, 0, 0, 0.1)",
            border: "2px solid rgba(255, 255, 255, 0.2)",
          }}
        >
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: "200px",
              height: "150px",
              objectFit: "cover",
              display: "block",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "8px",
              left: "8px",
              background: "rgba(0, 0, 0, 0.6)",
              color: "white",
              padding: "4px 8px",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: "500",
              backdropFilter: "blur(5px)",
            }}
          >
            📹 You
          </div>
        </div>
      </div>

      {/* 底部控制栏 */}
      <div
        style={{
          background: "rgba(0, 0, 0, 0.2)",
          backdropFilter: "blur(10px)",
          padding: "25px",
          textAlign: "center",
          borderTop: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <button
          onClick={handleLeave}
          style={{
            background: "linear-gradient(45deg, #ef4444, #dc2626)",
            color: "white",
            border: "none",
            borderRadius: "50px",
            padding: "15px 30px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
            boxShadow: "0 8px 20px rgba(239, 68, 68, 0.3)",
            transition: "all 0.3s ease",
            display: "inline-flex",
            alignItems: "center",
            gap: "10px",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 12px 25px rgba(239, 68, 68, 0.4)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 8px 20px rgba(239, 68, 68, 0.3)";
          }}
        >
          ❌ Leave Call
        </button>
      </div>

      {/* CSS动画 */}
      <style jsx>{`
        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
};

export default VideoCallPage;
